/* eslint-disable max-lines-per-function */
import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import {
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
} from "@mui/material";
import { FormikConfig } from "formik";
import { getPropertyDigestById, updatePropertyDailyDigest, updatePropertyDailyDigestVariables } from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { CCEmailForm } from "./cc-email-form";

const SUCCESS_MESSAGE = "Success";
const INVALID_EMAIL_MESSAGE = "Invalid Email Format";
const ERROR_MESSAGE = "Error updating email, please support@havenconnect.com for assistance.";

const PROPERTY = gql`
  query getPropertyDigestById($id: ID!) {
    property(id: $id) {
      id
      email
      submissionDigestEnabled
    }
  }
`;

const UPDATE_PROPERTY_DIGEST = gql`
  mutation updatePropertyDailyDigest($id: ID!, $data: PropertyUpdateInput!) {
    updatePropertyDailyDigest(id: $id, data: $data) {
      id
      email
      submissionDigestEnabled
    }
  }
`;

type Props = {
  propertyId: string;
}

export const DailyDigestForm : React.FC<Props> = ({ propertyId }) => {
  const { openSnackbar } = useSnackbar();

  const [updatePropertySettings] =
    useMutation<updatePropertyDailyDigest,
    updatePropertyDailyDigestVariables>(UPDATE_PROPERTY_DIGEST);
  const { data, loading, error } = useQuery<getPropertyDigestById>(PROPERTY, {
    fetchPolicy: "cache-first",
    variables: { id: propertyId },
  });

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;
  const { property } = data;

  const enabled = property.submissionDigestEnabled;

  const toggleEnabled = (isEnabled: boolean) => {
    if (!property.email && isEnabled) {
      openSnackbar({ message: INVALID_EMAIL_MESSAGE, status: "error" });
      return;
    }

    updatePropertySettings({
      variables: {
        id: property.id,
        data: {
          submissionDigestEnabled: isEnabled,
        },
      },
    })
      .then(() => {
        openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
      });
  };

  const updateEmail: FormikConfig<{email: string}>["onSubmit"] = (values) => {
    const isEnabled = !!values.email && enabled;

    updatePropertySettings({
      variables: {
        id: property.id,
        data: {
          email: values.email,
          submissionDigestEnabled: isEnabled,
        },
      },
    })
      .then(() => {
        openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: ERROR_MESSAGE, status: "error", duration: 4000 });
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6">Daily Digest Settings</Typography>
        <Typography variant="subtitle2">Define the email address for the daily digest.</Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <CCEmailForm initialEmail={property.email} onSubmit={updateEmail} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControl>
          <FormControlLabel
            labelPlacement="start"
            label="Receive Daily Submission Email Digest"
            control={<Switch onClick={() => toggleEnabled(!enabled)} checked={enabled} />}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};
